import type { ImportStatRow } from "../../../api/client.ts";
import { formatNumber, percent } from "../../../utils/format.ts";
import Panel from "../../Panel.tsx";
import RelativeTime from "../../RelativeTime.tsx";
import FigureSections, { type FigureSection } from "./FigureSections.tsx";
import { bySource, latestYear, sourceLabel, statRows, sumOf } from "./importStats.ts";

interface Props {
  stats: ImportStatRow[];
  refreshedAt: string | null;
}

function topSources(rows: ImportStatRow[], n = 3): string {
  return bySource(rows)
    .slice(0, n)
    .map((s) => `${sourceLabel(s.source)} ${formatNumber(s.total)}`)
    .join(" · ");
}

function asOf(refreshedAt: string | null) {
  return refreshedAt ? (
    <>
      Counted <RelativeTime value={refreshedAt} />
    </>
  ) : (
    "Not counted yet"
  );
}

/** Financials, tax, exchange rates, registry events and ownership. */
export function CompanyDataPanel({ stats, refreshedAt }: Props) {
  const accounts = statRows(stats, "financials", ["annual_accounts"]);
  const seFiles = statRows(stats, "financials", ["swedish_annual_report_files"]);
  const tax = statRows(stats, "tax");
  const fx = statRows(stats, "fx");
  const events = statRows(stats, "registry_event");
  const owners = statRows(stats, "shareholding");
  const relations = statRows(stats, "relation");
  const fxPending = sumOf(fx, "total") - sumOf(fx, "matched");
  const recentYear = new Date().getFullYear() - 1;

  const sections: FigureSection[] = [
    {
      title: "Financials and tax",
      figures: [
        {
          label: "Annual accounts",
          value: sumOf(accounts, "total"),
          added: sumOf(accounts, "added_24h"),
          caption: `${formatNumber(sumOf(accounts, "matched"))} companies with ${recentYear} accounts`,
        },
        {
          label: "Swedish annual report files",
          value: sumOf(seFiles, "total"),
          caption: `${formatNumber(sumOf(seFiles, "matched"))} parsed`,
        },
        {
          label: "Tax assessments",
          value: sumOf(tax, "total"),
          added: sumOf(tax, "added_24h"),
          caption: latestYear(tax) ? `Newest tax year ${latestYear(tax)}` : undefined,
        },
        {
          label: "Exchange rates",
          value: sumOf(fx, "total"),
          caption: fxPending > 0 ? `${formatNumber(fxPending)} waiting to be applied` : "All applied to companies",
        },
      ],
    },
    {
      title: "Registry events",
      figures: [
        {
          label: "Events",
          value: sumOf(events, "total"),
          added: sumOf(events, "added_24h"),
          caption: topSources(events),
        },
        {
          label: "Linked to a company",
          value: sumOf(events, "matched"),
          caption: percent(sumOf(events, "matched"), sumOf(events, "total")),
        },
      ],
    },
    {
      title: "Roles and ownership",
      figures: [
        {
          label: "Active roles",
          value: sumOf(relations, "matched"),
          added: sumOf(relations, "added_24h"),
          caption: topSources(relations),
        },
        {
          label: "Shareholdings",
          value: sumOf(owners, "total"),
          caption: latestYear(owners) ? `Snapshot ${latestYear(owners)}` : undefined,
        },
      ],
    },
  ];

  return (
    <Panel title="Company data" subtitle={asOf(refreshedAt)} fill>
      <FigureSections sections={sections} />
    </Panel>
  );
}

/** People, contact details, CRM records, Notion and bought contact data. */
export function PeopleAndCrmPanel({ stats, refreshedAt }: Props) {
  const people = statRows(stats, "contact", ["person"]);
  const channels = statRows(stats, "contact_channel");
  const profiles = statRows(stats, "contact_profile");
  const crm = (source: string) => statRows(stats, "crm", [source]);
  const activities = statRows(stats, "crm_activity");
  const notionPages = statRows(stats, "notion", ["page"]);
  const revisions = statRows(stats, "notion", ["revisions"]);
  const enrichment = statRows(stats, "contact_enrichment");
  const matched = (rows: ImportStatRow[]) =>
    `${percent(sumOf(rows, "matched"), sumOf(rows, "total"))} matched to the master data`;

  const sections: FigureSection[] = [
    {
      title: "People",
      figures: [
        { label: "People", value: sumOf(people, "total"), added: sumOf(people, "added_24h") },
        {
          label: "Emails and phones",
          value: sumOf(statRows(stats, "contact_channel", ["email", "phone"]), "total"),
          caption: topSources(channels),
        },
        {
          label: "Profiles",
          value: sumOf(profiles, "total"),
          added: sumOf(profiles, "added_24h"),
          caption: topSources(profiles),
        },
        {
          label: "Bought from providers",
          value: sumOf(enrichment, "matched"),
          caption: `${formatNumber(sumOf(enrichment, "total"))} jobs · ${topSources(enrichment)}`,
        },
      ],
    },
    {
      title: "CRM",
      figures: [
        { label: "Companies", value: sumOf(crm("companies"), "total"), caption: matched(crm("companies")) },
        { label: "Contacts", value: sumOf(crm("contacts"), "total"), caption: matched(crm("contacts")) },
        { label: "Deals", value: sumOf(crm("deals"), "total"), added: sumOf(crm("deals"), "added_24h") },
        { label: "Leads", value: sumOf(crm("leads"), "total"), added: sumOf(crm("leads"), "added_24h") },
        {
          label: "Activities",
          value: sumOf(activities, "total"),
          added: sumOf(activities, "added_24h"),
          caption: topSources(activities),
        },
        { label: "Sequences", value: sumOf(crm("sequences"), "total") },
      ],
    },
    {
      title: "Notion",
      figures: [
        { label: "Pages", value: sumOf(notionPages, "matched"), added: sumOf(notionPages, "added_24h") },
        { label: "Revisions", value: sumOf(revisions, "total"), added: sumOf(revisions, "added_24h") },
      ],
    },
  ];

  return (
    <Panel title="People and CRM" subtitle={asOf(refreshedAt)} fill>
      <FigureSections sections={sections} />
    </Panel>
  );
}
